/**
 * Styles de carte disponibles pour MapLibre
 *
 * Pour changer le style, modifier CURRENT_MAP_STYLE dans MapLibreView.tsx
 *
 * Sources communautaires :
 * - VersaTiles: https://github.com/versatiles-org/versatiles-style (colorful, pastel, etc.)
 * - OSM Liberty: https://github.com/maputnik/osm-liberty
 * - OpenFreeMap: https://openfreemap.org/quick_start/
 * - Awesome MapLibre: https://github.com/maplibre/awesome-maplibre
 */

export const MAP_STYLES = {
  // ============================================
  // STYLES GRATUITS (pas de clé API requise)
  // ============================================

  /**
   * OpenStreetMap - Style actuel (basique, moche)
   * ✅ Gratuit illimité
   * ❌ Raster, pas personnalisable, look daté
   */
  OSM_BASIC: {
    name: 'OpenStreetMap Basic',
    type: 'raster' as const,
    style: {
      version: 8 as const,
      sources: {
        osm: {
          type: 'raster' as const,
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors'
        }
      },
      layers: [{ id: 'osm', type: 'raster' as const, source: 'osm' }]
    },
    preview: '🗺️ Basique (ancien)'
  },

  // --------------------------------------------
  // CARTO - Gratuit illimité, très fiable
  // --------------------------------------------

  /**
   * Carto Voyager - Style coloré et moderne
   * ✅ Gratuit illimité, vectoriel
   * ✅ Look moderne, bon pour les apps grand public
   */
  CARTO_VOYAGER: {
    name: 'Carto Voyager',
    type: 'vector' as const,
    style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
    preview: '🎨 Coloré moderne'
  },

  /**
   * Carto Positron - Style clair minimaliste
   * ✅ Gratuit illimité, vectoriel
   * ✅ Très épuré, met en valeur les markers
   */
  CARTO_POSITRON: {
    name: 'Carto Positron',
    type: 'vector' as const,
    style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
    preview: '⚪ Light minimaliste'
  },

  /**
   * Carto Dark Matter - Style sombre
   * ✅ Gratuit illimité, vectoriel
   * ✅ Parfait pour dark mode
   */
  CARTO_DARK: {
    name: 'Carto Dark Matter',
    type: 'vector' as const,
    style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
    preview: '🌙 Dark mode'
  },

  // --------------------------------------------
  // OPENFREEMAP - 100% gratuit, communautaire
  // https://openfreemap.org
  // --------------------------------------------

  /**
   * OpenFreeMap Liberty - Style libre très populaire
   * ✅ 100% gratuit, pas de limites, pas de tracking
   * ✅ Basé sur OSM Liberty, très joli
   * Source: https://github.com/maputnik/osm-liberty
   */
  OPENFREEMAP_LIBERTY: {
    name: 'OpenFreeMap Liberty',
    type: 'vector' as const,
    style: 'https://tiles.openfreemap.org/styles/liberty',
    preview: '🗽 Liberty (populaire)'
  },

  /**
   * OpenFreeMap Bright - Style lumineux et clair
   * ✅ 100% gratuit, pas de limites
   * ✅ Couleurs vives, très lisible
   */
  OPENFREEMAP_BRIGHT: {
    name: 'OpenFreeMap Bright',
    type: 'vector' as const,
    style: 'https://tiles.openfreemap.org/styles/bright',
    preview: '☀️ Bright (lumineux)'
  },

  /**
   * OpenFreeMap Positron - Style épuré light
   * ✅ 100% gratuit
   * ✅ Minimaliste, parfait pour data viz
   */
  OPENFREEMAP_POSITRON: {
    name: 'OpenFreeMap Positron',
    type: 'vector' as const,
    style: 'https://tiles.openfreemap.org/styles/positron',
    preview: '🔘 Positron (épuré)'
  },

  // --------------------------------------------
  // VERSATILES - Styles artistiques et colorés
  // https://github.com/versatiles-org/versatiles-style
  // 100% gratuit, open source, très beaux styles !
  // --------------------------------------------

  /**
   * VersaTiles Colorful - Style très coloré et vivant
   * ✅ 100% gratuit, open source
   * ✅ Couleurs vives, parfait pour apps grand public
   */
  VERSATILES_COLORFUL: {
    name: 'VersaTiles Colorful',
    type: 'vector' as const,
    style: 'https://tiles.versatiles.org/assets/styles/colorful/style.json',
    preview: '🌈 Colorful (vivant)'
  },

  /**
   * VersaTiles Neutrino - Style light pastel
   * ✅ 100% gratuit, open source
   * ✅ Tons pastels, très doux
   */
  VERSATILES_NEUTRINO: {
    name: 'VersaTiles Neutrino',
    type: 'vector' as const,
    style: 'https://tiles.versatiles.org/assets/styles/neutrino/style.json',
    preview: '🍦 Neutrino (pastel)'
  },

  /**
   * VersaTiles Eclipse - Style sombre élégant
   * ✅ 100% gratuit, open source
   * ✅ Dark mode stylé
   */
  VERSATILES_ECLIPSE: {
    name: 'VersaTiles Eclipse',
    type: 'vector' as const,
    style: 'https://tiles.versatiles.org/assets/styles/eclipse/style.json',
    preview: '🌑 Eclipse (dark élégant)'
  },

  /**
   * VersaTiles Graybeard - Style gris sophistiqué
   * ✅ 100% gratuit, open source
   * ✅ Look pro, tons gris
   */
  VERSATILES_GRAYBEARD: {
    name: 'VersaTiles Graybeard',
    type: 'vector' as const,
    style: 'https://tiles.versatiles.org/assets/styles/graybeard/style.json',
    preview: '🎩 Graybeard (gris pro)'
  },

  // ============================================
  // STYLES FREEMIUM (clé API requise, gratuit limité)
  // ============================================

  /**
   * MapTiler Streets - Style pro moderne
   * ⚠️ Gratuit jusqu'à 100k requêtes/mois
   * ✅ Très pro, personnalisable
   * Nécessite: REACT_APP_MAPTILER_KEY
   */
  MAPTILER_STREETS: {
    name: 'MapTiler Streets',
    type: 'vector' as const,
    style: (key: string) => `https://api.maptiler.com/maps/streets-v2/style.json?key=${key}`,
    preview: '🏙️ Pro moderne',
    requiresKey: 'REACT_APP_MAPTILER_KEY'
  },

  /**
   * MapTiler Dataviz - Style épuré pour data
   * ⚠️ Gratuit jusqu'à 100k requêtes/mois
   * ✅ Très épuré, parfait pour mettre en valeur les events
   */
  MAPTILER_DATAVIZ: {
    name: 'MapTiler Dataviz',
    type: 'vector' as const,
    style: (key: string) => `https://api.maptiler.com/maps/dataviz/style.json?key=${key}`,
    preview: '📊 Dataviz épuré',
    requiresKey: 'REACT_APP_MAPTILER_KEY'
  },

  /**
   * Stadia Alidade Smooth - Style très moderne
   * ⚠️ Gratuit jusqu'à 200k requêtes/mois
   * ✅ Ultra moderne et smooth
   * Nécessite: REACT_APP_STADIA_KEY
   */
  STADIA_SMOOTH: {
    name: 'Stadia Alidade Smooth',
    type: 'vector' as const,
    style: (key: string) => `https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=${key}`,
    preview: '✨ Ultra smooth',
    requiresKey: 'REACT_APP_STADIA_KEY'
  },

  /**
   * Stadia Outdoors - Style aventure/nature
   * ⚠️ Gratuit jusqu'à 200k requêtes/mois
   * ✅ Parfait pour events outdoor
   */
  STADIA_OUTDOORS: {
    name: 'Stadia Outdoors',
    type: 'vector' as const,
    style: (key: string) => `https://tiles.stadiamaps.com/styles/outdoors.json?api_key=${key}`,
    preview: '🏕️ Outdoor/Nature',
    requiresKey: 'REACT_APP_STADIA_KEY'
  },
} as const;

// Style par défaut recommandé (gratuit et joli)
export const DEFAULT_MAP_STYLE = MAP_STYLES.CARTO_VOYAGER;

// Helper pour obtenir le style avec clé API si nécessaire
export function getMapStyle(styleKey: keyof typeof MAP_STYLES): string | object {
  const style = MAP_STYLES[styleKey];

  if (typeof style.style === 'function') {
    const envKey = style.requiresKey;
    const apiKey = envKey ? process.env[envKey] : undefined;

    if (!apiKey) {
      console.warn(`⚠️ Style ${style.name} nécessite ${envKey}. Fallback vers Carto Voyager.`);
      return MAP_STYLES.CARTO_VOYAGER.style;
    }

    return style.style(apiKey);
  }

  return style.style;
}

export type MapStyleKey = keyof typeof MAP_STYLES;
